import { queryAll, queryFirst } from '../domUtil';

/**
 * IMPORTANT NOTE: 
 * In case you are using this module - please use a require syntax to import a module into JavaScript files 
 * PLEASE AVOID syntax - import {} from
 * Current build tool breaks module import via ES6 import
 */

const CSRF_REFRESH_URL_SELECTOR = 'div.js-ajax-data';
const CSRF_INPUT_SELECTOR = `input[name*="csrf_token"], ${CSRF_REFRESH_URL_SELECTOR}`;
const DEFAULT_THRESHOLD_MS = 25 * 60 * 1000; // 25 minutes
const SINGLETON_KEY = Symbol.for('lp.csrfRefresh');

/**
 * Resolve the global root (browser `window`, else `globalThis`).
 * @returns {Object} The global object.
 */
function getRoot() {
    return typeof window !== 'undefined' ? window : globalThis;
}

/**
 * Build a fresh instance holding all state and behavior. Only the first
 * bundle to load ever runs this; every other bundle reuses the result.
 * @returns {{ initCsrfRefresh: Function, ensureFreshCsrfToken: Function }}
 */
function createInstance() {
    const state = {
        initializationTime: null,
        inFlight: null,
        visibilityListenerBound: false,
        threshold: DEFAULT_THRESHOLD_MS,
        refreshUrl: null
    };

    /**
     * Find all CSRF token holders in the DOM.
     * @returns {Element[]} Matching token holder elements.
     */
    function scanCsrfInputs() {
        return queryAll(CSRF_INPUT_SELECTOR);
    }

    /**
     * POST to the refresh endpoint to obtain a new CSRF token.
     * @returns {Promise<string|null>} The new token, or null on failure.
     */
    async function performRefresh() {
        try {
            const response = await fetch(state.refreshUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!response.ok) {
                return null;
            }

            const data = await response.json();

            if (data && data.error) {
                return null;
            }

            return (data && data.csrf && data.csrf.token) || (data && data.token) || null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Write the token into every CSRF holder element.
     * @param {string} token The CSRF token to apply.
     * @returns {void}
     */
    function updateElementsWithToken(token) {
        if (!token) { return }
        scanCsrfInputs().forEach((elem) => {
            switch (elem.tagName) {
                case "INPUT":
                    elem.value = token;
                    break;
                default:
                    elem.dataset.csrfTokenValue = token;
            }
        });
    }

    /**
     * Bind a one-time listener that re-checks token freshness on tab re-focus.
     * @returns {void}
     */
    function bindVisibilityListener() {
        if (state.visibilityListenerBound || typeof document === 'undefined') return;

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                ensureFreshCsrfToken();
            }
        });

        state.visibilityListenerBound = true;
    }

    /**
     * Initialize the singleton once at bootstrap: resolve the refresh URL,
     * record the token age baseline, and bind the visibility listener.
     * @returns {void}
     */
    function initCsrfRefresh() {
        const configDiv = queryFirst(CSRF_REFRESH_URL_SELECTOR);

        state.refreshUrl = configDiv?.dataset.csrfRefreshToken || null;
        state.threshold = Number(configDiv?.dataset?.configThreshold) || state.threshold;
        state.initializationTime = state.refreshUrl && scanCsrfInputs().length > 0 ? Date.now() : null;

        bindVisibilityListener();
    }

    /**
     * Ensure a fresh token before a CSRF-protected request. Shares one
     * in-flight promise so concurrent callers never issue duplicate refreshes.
     *
     * @returns {Promise<boolean>} `true` if the token is fresh (or refreshed),
     *   `false` if uninitialized or the refresh failed.
     */
    async function ensureFreshCsrfToken() {
        if ((Date.now() - state.initializationTime) < state.threshold) {
            return Promise.resolve(true);
        }

        if (state.inFlight) return state.inFlight;

        if (!state.initializationTime) {
            return Promise.resolve(false);
        }

        state.inFlight = (async () => {
            const tokenValue = await performRefresh();
            if (!tokenValue) {
                return false;
            }

            updateElementsWithToken(tokenValue);

            state.initializationTime = Date.now();

            return true;
        })().finally(() => {
            state.inFlight = null;
        });

        return state.inFlight;
    }

    return { initCsrfRefresh, ensureFreshCsrfToken };
}

/**
 * Return the one shared instance, creating it on first access. The instance
 * lives on `window` under a registry symbol so every webpack bundle (one
 * entry per top-level client JS file) and any non-bundled script resolve to
 * the exact same object and state.
 * @returns {{ initCsrfRefresh: Function, ensureFreshCsrfToken: Function }}
 */
function getInstance() {
    const root = getRoot();

    if (!root[SINGLETON_KEY]) {
        root[SINGLETON_KEY] = createInstance();
    }

    return root[SINGLETON_KEY];
}

/**
 * @param {Object} [options] See instance initCsrfRefresh.
 * @returns {void}
 */
export function initCsrfRefresh(options) {
    return getInstance().initCsrfRefresh(options);
}

/**
 * @returns {Promise<boolean>} See instance ensureFreshCsrfToken.
 */
export async function ensureFreshCsrfToken() {
    return await getInstance().ensureFreshCsrfToken();
}

/**
 * Refresh the token if it is stale, then read the current value out of the DOM. Pairs the
 * pre-flight with the read so a caller cannot accidentally do one without the other.
 *
 * @returns {Promise<string>} Current csrf_token value, or '' if the data element is absent.
 */
export async function getFreshCsrfToken() {
    await ensureFreshCsrfToken();

    return queryFirst(CSRF_REFRESH_URL_SELECTOR)?.dataset?.csrfTokenValue || '';
}
